<template>
  <section id="section-customer-reviews">
    <SectionHeading
      v-if="props.header"
      id="toc-customer-reviews"
      class="heading"
      icon="icon-multiple-users-1"
      title="All trip reviews"
    />

    <div class="reviews-container" :class="{ scrollable: props.expand }">
      <div class="sidebar">
        <div class="sidebar-title">Most mentioned in our community</div>
        <div class="highlights">
          <div class="highlight-item" v-for="item in store.customerReviewSummary" :key="item">
            <i class="icon icon-check-circle-1"></i>
            <span>{{ item }}</span>
          </div>
        </div>
      </div>

      <div class="reviews-list">
        <div class="reviews-list-title">What travelers shared from their journeys</div>

        <div v-if="store.loading.reviews" class="loading reviews">
          <div class="review" v-for="i in 3" :key="i">
            <div v-if="desktop" class="review-header">
              <Skeleton width="48px" height="48px" borderRadius="100%" />
              <div class="reviewer-info gap">
                <Skeleton width="120px" height="16px" borderRadius="8px" />
                <Skeleton width="80px" height="16px" borderRadius="8px" />
                <Skeleton width="60px" height="16px" borderRadius="8px" />
              </div>
            </div>
            <div v-if="mobile" class="review-header">
              <div class="flex-row">
                <Skeleton width="48px" height="48px" borderRadius="100%" />
                <div class="reviewer-info gap">
                  <Skeleton width="120px" height="16px" borderRadius="8px" />
                  <Skeleton width="80px" height="16px" borderRadius="8px" />
                </div>
              </div>
              <Skeleton width="150px" height="24px" borderRadius="8px" />
            </div>
            <Skeleton height="28px" borderRadius="10px" />
            <Skeleton height="36px" width="60%" borderRadius="10px" />
          </div>
        </div>

        <div v-else-if="store.reviews.length > 0" class="reviews" :class="{ scrollable: props.expand }">
          <div class="review" v-for="review in reviews" :key="review.id">
            <div class="review-header">
              <div class="flex-row">
                <img v-if="review.guestPhotoUrl" class="avatar" :src="review.guestPhotoUrl" :alt="review.guestName" />
                <div class="reviewer-info">
                  <div class="reviewer-name">{{ review.guestName }}</div>
                  <div class="review-date">{{ formatDate(review.submittedAt) }}</div>
                </div>
              </div>
              <CustomerReviewStarRating :rating="review.score" />
            </div>

            <div v-if="review.stoodOut">
              {{ review.stoodOut }}
            </div>

            <a v-if="review.title && review.url" :href="review.url" class="trip-link">
              {{ review.title }}
              <i class="icon icon-arrow-right-1"></i>
            </a>
          </div>
        </div>

        <div v-else class="no-reviews">No reviews yet</div>

        <Button
          id="see-all-reviews"
          v-if="hasMore && !store.loading.reviews && !props.expand"
          @click="showMore"
          color="green"
          invert
          class="see-all-btn"
        >
          See all reviews
        </Button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useItineraryStore } from '@/stores/itinerary.js'
import SectionHeading from '@/modules/Itinerary/Components/SectionHeading.vue'
import { Skeleton } from '@/components'
import { useScrollLock } from '@vueuse/core'
import moment from 'moment'
import { useResponsive } from '@/helpers/vueHelpers'
import Button from '@/components/Theme2/Button.vue'
import CustomerReviewStarRating from '@/modules/Itinerary/Components/CustomerReviewStarRating.vue'

const REVIEW_LIMIT = 3
const props = defineProps<{
  expand?: boolean
  header?: boolean
}>()

const locked = useScrollLock(document, false)
const store = useItineraryStore()
const { mobile, desktop } = useResponsive()
store.monitorVisibility('customer-reviews', '#section-customer-reviews')

watch(
  () => store.isReviewModalOpen,
  open => {
    locked.value = open
  },
)

const hasMore = computed(() => {
  return store.reviews.length > 0
})
const reviews = computed(() => {
  return props.expand ? store.reviews : store.reviews.slice(0, REVIEW_LIMIT)
})

const showMore = () => {
  store.fetchReviews({ productId: store.id, itineraryId: store.id })
  store.openReviewModel()
  locked.value = true
}

const formatDate = dateString => {
  if (!dateString) return ''
  return moment(dateString).fromNow()
}
</script>

<style lang="scss" scoped>
#section-customer-reviews {
  @include flex-column(20px);
}

.heading {
  margin-bottom: 0;
}

.reviews-container {
  display: grid;
  grid-template-columns: 2fr 5fr;
  gap: 32px;

  @include mobile {
    grid-template-columns: 1fr;
    gap: $spacing-4;

    &.scrollable {
      @include scrollbar;
      padding-right: $spacing-2;
      max-height: 82vh;
      overflow-y: auto;
    }
  }
}

.sidebar {
  background: $grey-25;
  padding: $spacing-6;
  border-radius: 15px;
  height: fit-content;

  .sidebar-title {
    font-weight: $font-semibold;
    margin-bottom: $spacing-4;
  }

  .highlights {
    @include flex-column(12px);
  }

  .highlight-item {
    display: flex;
    gap: $spacing-2;

    .icon {
      color: $brand-tertiary;
      font-size: $text-base;
    }
  }
}

.reviews-list {
  @include flex-column($spacing-4);

  .reviews-list-title {
    font-weight: $font-semibold;
  }
}

.loading,
.no-reviews {
  text-align: center;
  color: $grey-75;
}

.reviews {
  @include flex-column($spacing-4);

  &.scrollable {
    @include desktop {
      @include scrollbar;
      padding-right: $spacing-4;
      max-height: 60vh;
      overflow-y: auto;
    }
  }
}

.review {
  @include flex-column($spacing-4);

  &:not(:last-child) {
    border-bottom: 1px solid $grey-50;
    padding-bottom: $spacing-4;
  }

  &:not(:first-child) {
    padding-top: $spacing-2;
  }

  .review-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    @include desktop {
      flex-direction: row;
    }

    @include mobile {
      flex-direction: column;
    }

    .avatar {
      width: $spacing-12;
      height: $spacing-12;
      border-radius: $rounded-max;
      object-fit: cover;
      background: $grey-25;
    }

    .reviewer-info {
      flex: 1;
      @include flex-column;

      &.gap {
        gap: $spacing-1;
      }

      .reviewer-name {
        font-weight: $font-semibold;
      }

      .review-date {
        font-size: $text-sm;
        color: $grey-100;
      }
    }
  }
}

.trip-link {
  font-size: $text-sm;
  font-weight: $font-semibold;
  color: $black;
  padding: $spacing-2 $spacing-3;
  background: $grey-25;
  border-radius: $spacing-2;
  transition: background 100ms ease;
  width: fit-content;

  &:hover {
    background: $green-50;
  }

  .icon {
    font-size: 12px;
  }
}

.see-all-btn {
  align-self: center;
}

.flex-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: $spacing-4;
}
</style>
